import React, { useState, useEffect } from "react";
import { Form, Button, Card, Row, Col, Spinner } from "react-bootstrap";
import {
  FaTag,
  FaBoxes,
  FaChevronLeft,
  FaPercentage,
  FaMoneyBill,
  FaLayerGroup,
} from "react-icons/fa";
import { useSelector } from "react-redux";

import { useCreateProductMutation } from "../slices/productApiSlice";
import { useGetCategoriesQuery } from "../slices/categoryApiSlice";

const CreateProductScreen = ({ navigate }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [price, setPrice] = useState("");
  const [shopPrice, setShopPrice] = useState("");
  const [cost, setCost] = useState("");
  const [countInStock, setCountInStock] = useState("");
  const [reOrderLevel, setReOrderLevel] = useState("");
  const [category, setCategory] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [discount, setDiscount] = useState("");

  // 🧾 GST
  const [hsn, setHsn] = useState("");
  const [gstRate, setGstRate] = useState("");
  const [cgst, setCgst] = useState("");
  const [sgst, setSgst] = useState("");
  const [igst, setIgst] = useState("");

  const [subcategoriesList, setSubcategoriesList] = useState([]);

  const { userInfo } = useSelector((state) => state.auth);

  const [createProduct, { isLoading }] = useCreateProductMutation();
  const { data: categories } = useGetCategoriesQuery();

  // Update subcategories when category changes
  useEffect(() => {
    if (!categories) return;
    const selected = categories.find((c) => c.name === category);
    setSubcategoriesList(selected?.subcategories || []);
    setSubcategory("");
  }, [category, categories]);

  // ✅ Auto-populate GST breakup
  useEffect(() => {
    if (!gstRate) return;

    const rate = Number(gstRate);
    if (isNaN(rate)) return;

    setIgst(rate);
    setCgst(rate / 2);
    setSgst(rate / 2);
  }, [gstRate]);

  const submitHandler = async (e) => {
    e.preventDefault();

    // ✅ Convert & normalize inputs (MODEL FORMAT)
    const formattedProduct = {
      vendorId: userInfo._id,

      // Identity
      name: name.trim(),
      description: description?.trim() || undefined,

      // Categorization
      category: category.trim(),
      subcategory: subcategory.trim(),

      // Pricing
      price: Number(price),
      shopPrice: Number(shopPrice),
      cost: cost ? Number(cost) : undefined,
      discount: discount ? Number(discount) : 0,

      // Inventory
      countInStock: Number(countInStock),
      reOrderLevel: Number(reOrderLevel),

      // GST (DENORMALIZED)
      hsn: hsn.trim(),
      gstRate: Number(gstRate),
      cgst: Number(cgst) || 0,
      sgst: Number(sgst) || 0,
      igst: Number(igst) || 0,
    };

    // 🔐 Required field validation (AFTER conversion)
    if (
      !formattedProduct.name ||
      !formattedProduct.price ||
      !formattedProduct.shopPrice ||
      !formattedProduct.countInStock ||
      !formattedProduct.reOrderLevel ||
      !formattedProduct.category ||
      !formattedProduct.subcategory ||
      !formattedProduct.hsn ||
      !formattedProduct.gstRate
    ) {
      alert("Please fill in all required fields.");
      return;
    }

    // 🧾 GST validation (MODEL RULE)
    const gstTotal =
      formattedProduct.cgst +
      formattedProduct.sgst +
      formattedProduct.igst;

    

    try {
  await createProduct(formattedProduct).unwrap();
  alert("Product created successfully!");

  // ❌ Removed navigate(-1)
} catch (err) {
  if (err?.name === "AbortError") return;
  alert(err?.data?.message || "Failed to create product");
}


  };

  return (
    <Row className="justify-content-center">
      <Col md={8} lg={6}>
        <Card className="shadow-lg border-0 mt-4">
          <Card.Header className="bg-primary text-white d-flex align-items-center">
            <FaChevronLeft
              className="me-2"
              style={{ cursor: "pointer" }}
              onClick={() => navigate(-1)}
            />
            <h5 className="mb-0">Create New Product</h5>
          </Card.Header>

          <Card.Body>
            <Form onSubmit={submitHandler}>
              <Form.Group className="mb-3">
                <Form.Label>
                  <FaTag className="me-2 text-info" /> Product Name *
                </Form.Label>
                <Form.Control
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label>Description</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaMoneyBill className="me-2 text-success" /> Price *
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={price}
                      onChange={(e) => setPrice(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Shop Price *</Form.Label>
                    <Form.Control
                      type="number"
                      value={shopPrice}
                      onChange={(e) => setShopPrice(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label>
                  <FaMoneyBill className="me-2 text-secondary" /> Cost (optional)
                </Form.Label>
                <Form.Control
                  type="number"
                  value={cost}
                  onChange={(e) => setCost(e.target.value)}
                />
              </Form.Group>

              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>
                      <FaBoxes className="me-2 text-warning" /> Stock Count *
                    </Form.Label>
                    <Form.Control
                      type="number"
                      value={countInStock}
                      onChange={(e) => setCountInStock(e.target.value)}
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label>Reorder Level *</Form.Label>
                    <Form.Control
                      type="number"
                      value={reOrderLevel}
                      onChange={(e) => setReOrderLevel(e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>

              {/* Category */}
              <Card className="mb-3 border-info">
                <Card.Header className="bg-info text-white d-flex align-items-center">
                  <FaLayerGroup className="me-2" />
                  Product Classification
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>Category *</Form.Label>
                    <Form.Select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                    >
                      <option value="">Select Category</option>
                      {(categories || []).map((cat) => (
                        <option key={cat._id} value={cat.name}>
                          {cat.name}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label>Subcategory *</Form.Label>
                    <Form.Select
                      value={subcategory}
                      onChange={(e) => setSubcategory(e.target.value)}
                      disabled={!category}
                    >
                      <option value="">Select Subcategory</option>
                      {(subcategoriesList || []).map((sub, idx) => (
                        <option key={idx} value={sub}>
                          {sub}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </Card.Body>
              </Card>

              {/* GST */}
              <Card className="mb-3 border-success">
                <Card.Header className="bg-success text-white">
                  GST Details
                </Card.Header>
                <Card.Body>
                  <Form.Group className="mb-3">
                    <Form.Label>HSN Code *</Form.Label>
                    <Form.Control
                      type="text"
                      value={hsn}
                      onChange={(e) => setHsn(e.target.value)}
                    />
                  </Form.Group>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>GST Rate % *</Form.Label>
                        <Form.Control
                          type="number"
                          value={gstRate}
                          onChange={(e) => setGstRate(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>IGST %</Form.Label>
                        <Form.Control
                          type="number"
                          value={igst}
                          onChange={(e) => setIgst(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>

                  <Row>
                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>CGST %</Form.Label>
                        <Form.Control
                          type="number"
                          value={cgst}
                          onChange={(e) => setCgst(e.target.value)}
                        />
                      </Form.Group>
                    </Col>

                    <Col md={6}>
                      <Form.Group className="mb-3">
                        <Form.Label>SGST %</Form.Label>
                        <Form.Control
                          type="number"
                          value={sgst}
                          onChange={(e) => setSgst(e.target.value)}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Card.Body>
              </Card>

              <Form.Group className="mb-4">
                <Form.Label>
                  <FaPercentage className="me-2 text-danger" /> Discount
                </Form.Label>
                <Form.Control
                  type="number"
                  value={discount}
                  onChange={(e) => setDiscount(e.target.value)}
                />
              </Form.Group>

              <div className="d-grid">
                <Button type="submit" disabled={isLoading}>
                  {isLoading ? (
                    <>
                      <Spinner size="sm" animation="border" /> Creating...
                    </>
                  ) : (
                    "Create Product"
                  )}
                </Button>
              </div>
            </Form>
          </Card.Body>
        </Card>
      </Col>
    </Row>
  );
};

export default CreateProductScreen;
